import React, { useCallback, useEffect, useRef, useState } from "react";
import constate from "constate";
import authApi from "../client-api/auth";
import { useTimer } from "react-use-precision-timer";
import { CircuitProgram, BasicProgram } from "../types";
import { getProgram } from "../client-api/program";
import dayjs from "dayjs";
import EventEmitter from "eventemitter3";
import toast from "react-hot-toast";
const preloadVideoList: {
  video: string;
  url: string;
}[] = [];
async function preloadVideo(src: string) {
  const res = await fetch(src);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function useAuth() {
  const emitter = useRef<EventEmitter>(new EventEmitter());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [did, setDid] = useState<"0" | "L1" | "L6" | "R1" | "R6" | "1">();
  const [classId, setClassId] = useState<number>();
  const [index, setIndex] = useState<number | undefined>();

  // const socket = useRef<WebSocket>(
  //   new WebSocket(process.env.REACT_APP_WEB_SOCKET_URL + "/websocket")
  // );
  const socket = useRef<WebSocket>();

  const [step, setStep] = useState(0);
  const [basicProgram, setBasicProgram] = useState<BasicProgram>();
  const [circuitProgram, setCircuitProgram] = useState<CircuitProgram>();
  const startTime = useRef<number>(0);

  const timer = useTimer({
    fireOnStart: false,
    startImmediately: false,
  });

  useEffect(() => {
    const connectSocket = () => {
      socket.current = new WebSocket(
        process.env.REACT_APP_WEB_SOCKET_URL + "/websocket"
      );
      socket.current?.addEventListener("open", (event) => {
        if (localStorage.getItem("token")) {
          socket.current?.send(
            JSON.stringify({
              type: "CREATE_SESSION",
              data: localStorage.getItem("token"),
            })
          );
        }
      });
      socket.current?.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(JSON.parse(event.data));
          if (data.type === "START_PROGRAM") {
            startTime.current = 0;
            setLoading(true);
            setStep(0);
            setBasicProgram(undefined);
            setCircuitProgram(undefined);
            setClassId(data.selectedClassId);
            const localDid = localStorage.getItem("did");
            getProgram(data.programId).then(async (program) => {
              if (program.programType === "BASIC") {
                const weightProgram = program as BasicProgram;

                if (localDid !== "0") {
                  const videoList = [
                    ...weightProgram.coolDownMovements.map(
                      (movement) => movement.movement.video
                    ),
                    ...weightProgram.warmUpMovements.map(
                      (movement) => movement.movement.video
                    ),
                    ...weightProgram.mainExers.map(
                      (movement) => movement.movement.video
                    ),
                  ];

                  const newVideoList = videoList.reduce(
                    (acc, cur) =>
                      acc.includes(cur) ||
                      preloadVideoList.find((video) => video.video === cur)
                        ? acc
                        : [...acc, cur],
                    [] as string[]
                  );

                  const _preloadVideoList = await Promise.all(
                    newVideoList.map(async (video) => {
                      const url = await preloadVideo(video);
                      return { video, url };
                    })
                  );
                  preloadVideoList.push(..._preloadVideoList);
                  weightProgram.coolDownMovements.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video
                      )!.url)
                  );
                  weightProgram.warmUpMovements.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video
                      )!.url)
                  );
                  weightProgram.mainExers.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video
                      )!.url)
                  );
                }

                setLoading(false);
                setBasicProgram(weightProgram);
              }
              if (
                program.programType === "CIRCUIT1" ||
                program.programType === "CIRCUIT2"
              ) {
                const circuitProgram = program as CircuitProgram;

                if (localDid !== "0") {
                  let videoList = [
                    ...circuitProgram.coolDownMovements.map(
                      (movement) => movement.movement.video
                    ),
                    ...circuitProgram.warmUpMovements.map(
                      (movement) => movement.movement.video
                    ),
                    ...circuitProgram.part1.movements.map(
                      (movement) => movement.video
                    ),
                    ...circuitProgram.part2.movements.map(
                      (movement) => movement.video
                    ),
                  ];

                  const newVideoList = videoList.reduce(
                    (acc, cur) =>
                      acc.includes(cur) ||
                      preloadVideoList.find((video) => video.video === cur)
                        ? acc
                        : [...acc, cur],
                    [] as string[]
                  );

                  const _preloadVideoList = await Promise.all(
                    newVideoList.map(async (video) => {
                      const url = await preloadVideo(video);
                      return { video, url };
                    })
                  );
                  preloadVideoList.push(..._preloadVideoList);

                  circuitProgram.coolDownMovements.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video
                      )!.url)
                  );
                  circuitProgram.warmUpMovements.map(
                    (movement) =>
                      (movement.movement.video = preloadVideoList.find(
                        (video) => video.video === movement.movement.video
                      )!.url)
                  );
                  circuitProgram.part1.movements.map(
                    (movement) =>
                      (movement.video = preloadVideoList.find(
                        (video) => video.video === movement.video
                      )!.url)
                  );
                  circuitProgram.part2.movements.map(
                    (movement) =>
                      (movement.video = preloadVideoList.find(
                        (video) => video.video === movement.video
                      )!.url)
                  );
                }

                setLoading(false);
                setCircuitProgram(circuitProgram);
              }
              setStep(1);
            });
          }
          if (data.type === "START_TIMER") {
            const time = data.startTime;
            timer.start();
            setStep(2);
          }
          if (data.type === "SET_STEP") {
            const time = data.startTime;
            setStep(data.step);
            startTime.current = time;
          }
          if (data.type === "START_COUNTDOWN") {
            emitter.current.emit("resume", data.startTime);
          }
          if (data.type === "STOP_COUNTDOWN") {
            emitter.current.emit("pause", data.startTime);
          }
          if (data.type === "CURRENT_INDEX") {
            console.log("CURRENT_INDEX", data.index);
            setIndex(data.index);
          }
        } catch (e) {
          console.log(e);
        }
      });
      socket.current?.addEventListener("close", (event) => {
        // retry
        socket.current?.close();
        setTimeout(() => {
          socket.current = new WebSocket(
            process.env.REACT_APP_WEB_SOCKET_URL + "/websocket"
          );
          connectSocket();
        }, 300);
        console.log("close");
      });
    };
    connectSocket();
    return () => {
      // socket.current?.close();
      // emitter.current.removeAllListeners();
    };
  }, []);

  const login = async (username: string, password: string, did: string) => {
    if (
      did !== "0" &&
      did !== "L1" &&
      did !== "L6" &&
      did !== "R1" &&
      did !== "R6" &&
      did !== "1"
    ) {
      toast("DID를 확인해주세요.");
      return;
    }
    try {
      const data = await authApi.login(username, password, did);
      localStorage.setItem("token", data.tokens.accessToken);
      localStorage.setItem("refreshToken", data.tokens.refreshToken);
      localStorage.setItem("did", did);
      localStorage.setItem("username", username);
      setDid(did as any);
      setIsAuthenticated(true);
      setStep(0);
      setBasicProgram(undefined);
      setCircuitProgram(undefined);
      socket.current?.send(
        JSON.stringify({
          type: "CREATE_SESSION",
          data: data.tokens.accessToken,
        })
      );
    } catch (e) {
      toast("아이디와 비밀번호를 확인해주세요.");
    }
  };

  const logout = async () => {
    authApi.logout().then();
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("did");
    setIsAuthenticated(false);
  };

  const refreshToken = async () => {
    // const data = await authApi.refreshToken();
    // localStorage.setItem("token", data.accessToken);
    // localStorage.setItem("refreshToken", data.refreshToken);
    setIsAuthenticated(true);
    setLoading(false);
    setDid(localStorage.getItem("did") as any);
    socket.current?.send(
      JSON.stringify({
        type: "CREATE_SESSION",
        data: localStorage.getItem("token"),
      })
    );
  };

  useEffect(() => {
    if (localStorage.getItem("token")) {
      (async () => {
        try {
          await refreshToken();
        } catch {
          setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
  }, []);

  const sendMessage = (message: string) => {
    socket.current?.send(
      JSON.stringify({
        type: "SEND_MESSAGE",
        data: message,
      })
    );
  };
  const startProgram = (
    programId: number,
    rowType: "300m" | "600m" | "1200m" | undefined,
    selectedClassId: number | undefined
  ) => {
    sendMessage(
      JSON.stringify({
        type: "START_PROGRAM",
        programId: programId,
        rowType: rowType,
        selectedClassId: selectedClassId,
      })
    );
  };
  const sendStartTimer = () => {
    sendMessage(
      JSON.stringify({
        type: "START_TIMER",
        startTime: dayjs().add(200, "ms").valueOf(),
      })
    );
  };

  const sendSetStep = (step: number) => {
    sendMessage(
      JSON.stringify({
        type: "SET_STEP",
        step: step,
        startTime: dayjs().add(200, "ms").valueOf(),
      })
    );
  };

  const sendStartCountDown = () => {
    sendMessage(
      JSON.stringify({
        type: "START_COUNTDOWN",
        startTime: dayjs().add(200, "ms").valueOf(),
      })
    );
  };

  const sendStopCountDown = () => {
    sendMessage(
      JSON.stringify({
        type: "STOP_COUNTDOWN",
        startTime: dayjs().add(200, "ms").valueOf(),
      })
    );
  };

  const sendCurrentIndex = (index: number | undefined) => {
    sendMessage(
      JSON.stringify({
        type: "CURRENT_INDEX",
        index: index,
      })
    );
  };

  return {
    isAuthenticated,
    login,
    logout,
    refreshToken,
    loading,
    did,
    socket,
    step,
    setStep,
    timer,
    basicProgram,
    circuitProgram,
    startProgram,
    sendStartTimer,
    sendSetStep,
    startTime,
    emitter,
    sendStartCountDown,
    sendStopCountDown,
    classId,
    sendCurrentIndex,
    index,
  };
}

// 2️⃣ Wrap your hook with the constate factory
const [AuthProvider, useAuthContext] = constate(useAuth);

export { AuthProvider, useAuthContext };
